import React, { useState, useEffect, useRef } from "react";
import "./MathPractice.css";
import { awardToken } from "../../awardtoken";
import { db } from "../../firebase";
import { doc, setDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../../components/useAuth";

const SOUND_SOURCES = ["/sounds/correct.mp3", "/sounds/correct.ogg", "/sounds/correct.wav"];
const DIGIT_OPTIONS = [
  { label: "Single‑digit (x × x)", value: 1 },
  { label: "Double‑digit (xx × xx)", value: 2 },
  { label: "Triple‑digit (xxx × xxx)", value: 3 },
];
const SHEET_SIZES = [10, 25, 50];

const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const genNum = (d) => (d === 1 ? rand(1, 9) : rand(10 ** (d - 1), 10 ** d - 1));
const genMulProblem = (d) => ({ a: genNum(d), b: genNum(d) });
const genSet = (n, d) => Array.from({ length: n }, () => genMulProblem(d));

const VerticalProblem = React.memo(({ a, b }) => {
  const aStr = String(a);
  const bStr = String(b);
  const len = Math.max(aStr.length, bStr.length);
  const line1 = " " + aStr.padStart(len, " ");
  const line2 = "×" + bStr.padStart(len, " ");
  const sep = "-".repeat(len + 1);
  return (
    <pre className="vertical-problem">
      {line1}{"\n"}{line2}{"\n"}{sep}
    </pre>
  );
});

const WSCol = ({ a, b }) => {
  const aStr = String(a);
  const bStr = String(b);
  const len = Math.max(aStr.length, bStr.length);
  return (
    <div className="math-col">
      <div className="math-top">{aStr.padStart(len, "\u00a0")}</div>
      <div className="math-bottom">×{bStr.padStart(len, "\u00a0")}</div>
      <div className="math-underline" />
      <div className="math-answer-space">&nbsp;</div>
    </div>
  );
};

const TOOL_TYPE = "MathMultiplicationPractice";

async function logMultiplicationPractice({ user, digits, numQuestions, correct, attempts, startTime, endTime }) {
  if (!user?.uid) {
    console.warn("No user UID! User:", user);
    return;
  }
  if (attempts === 0) {
    console.warn("No attempts, nothing to log.");
    return;
  }
  try {
    await setDoc(
      doc(db, "users", user.uid, "practiceRecords", TOOL_TYPE),
      {
        toolType: TOOL_TYPE,
        topic: `${digits}-digit multiplication`,
        numQuestions,
        correct,
        attempts,
        timestamp: serverTimestamp(),
        startedAt: startTime,
        finishedAt: endTime,
        durationSec: Math.round((endTime - startTime) / 1000),
      },
      { merge: true }
    );
  } catch (e) {
    console.error("Error writing session log:", e);
  }
}

export default function MathMultiplicationPractice({ onAwardTokens } = {}) {
  const [digits, setDigits] = useState(1);
  const [curr, setCurr] = useState(() => genMulProblem(1));
  const [ans, setAns] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [sheet, setSheet] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [waitingForNext, setWaitingForNext] = useState(false);

  const { user } = useAuth();
  const [sessionStart, setSessionStart] = useState(Date.now());
  const audioRef = useRef(null);
  const tokensAwarded = useRef(0);
  const didMountRef = useRef(false);

  // 🟢 Track session stats using a ref
  const sessionStats = useRef({ attempts: 0, correct: 0 });

  // Award token every 10 correct
  useEffect(() => {
    if (correct >= (tokensAwarded.current + 1) * 10) {
      awardToken();
      tokensAwarded.current += 1;
      if (onAwardTokens) onAwardTokens(1);
    }
  }, [correct, onAwardTokens]);

  // Digit change: log session, reset counters, new problem
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      setCurr(genMulProblem(digits));
      setAns("");
      return;
    }
    // LOG SESSION using sessionStats ref
    const endTime = Date.now();
    if (sessionStats.current.attempts > 0) {
      logMultiplicationPractice({
        user,
        digits,
        numQuestions: sessionStats.current.attempts,
        correct: sessionStats.current.correct,
        attempts: sessionStats.current.attempts,
        startTime: sessionStart,
        endTime,
      });
    }
    setSessionStart(Date.now());
    setAttempts(0);
    setCorrect(0);
    sessionStats.current = { attempts: 0, correct: 0 };
    setCurr(genMulProblem(digits));
    setAns("");
    // eslint-disable-next-line
  }, [digits]);

  // Log on unmount
  useEffect(() => {
    return () => {
      const endTime = Date.now();
      if (sessionStats.current.attempts > 0) {
        logMultiplicationPractice({
          user,
          digits,
          numQuestions: sessionStats.current.attempts,
          correct: sessionStats.current.correct,
          attempts: sessionStats.current.attempts,
          startTime: sessionStart,
          endTime,
        });
      }
    };
    // eslint-disable-next-line
  }, []);

  // Setup audio
  useEffect(() => {
    const audio = new Audio();
    for (const src of SOUND_SOURCES) {
      const type = src.endsWith(".mp3")
        ? "audio/mpeg"
        : src.endsWith(".ogg")
        ? "audio/ogg"
        : "audio/wav";
      if (audio.canPlayType(type)) {
        audio.src = src;
        break;
      }
    }
    audioRef.current = audio;
  }, []);

  // Handle answer submit
const submit = (e) => {
  e.preventDefault();
  if (!ans.trim() || waitingForNext) return;
  const right = Number(ans) === curr.a * curr.b;
  const nextAttempts = attempts + 1;
  const nextCorrect = right ? correct + 1 : correct;

  setAttempts(nextAttempts);
  setCorrect(nextCorrect);
  sessionStats.current = { attempts: nextAttempts, correct: nextCorrect };
  console.log("SUBMIT: sessionStats.current = ", sessionStats.current);

  // 🔥 Log every answer—just like addition!
  const endTime = Date.now();
  logMultiplicationPractice({
    user,
    digits,
    numQuestions: nextAttempts,
    correct: nextCorrect,
    attempts: nextAttempts,
    startTime: sessionStart,
    endTime,
  });

  if (right) {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
    setCurr(genMulProblem(digits));
    setAns("");
    setFeedback(null);
    setWaitingForNext(false);
  } else {
    setFeedback(`❌ The correct answer is: ${curr.a * curr.b}`);
    setWaitingForNext(true);
  }
};

  // Finish session button for manual session ending
  const finishSession = () => {
    const endTime = Date.now();
    if (sessionStats.current.attempts > 0) {
      logMultiplicationPractice({
        user,
        digits,
        numQuestions: sessionStats.current.attempts,
        correct: sessionStats.current.correct,
        attempts: sessionStats.current.attempts,
        startTime: sessionStart,
        endTime,
      });
      setSessionStart(Date.now());
      setAttempts(0);
      setCorrect(0);
      sessionStats.current = { attempts: 0, correct: 0 };
    }
  };

  const startSheet = (n) => { setSheet(genSet(n, digits)); setTimeout(() => window.print(), 100); };

  if (sheet) return (
    <div className="worksheet">
      <h2 className="no-print">Multiplication Worksheet – {digits}-digit</h2>
      <button className="no-print" onClick={() => setSheet(null)}>Back to Practice</button>
      <div className="sheet-grid">{sheet.map((p, i) => <WSCol key={i} {...p} />)}</div>
    </div>
  );

  return (
    <div className="practice-container">
      <h2>Multiplication Practice</h2>
      <fieldset>
        <legend>Choose number size:</legend>
        {DIGIT_OPTIONS.map(({ label, value }) => (
          <label key={value} style={{ marginRight: "1rem" }}>
            <input
              type="radio"
              checked={digits === value}
              onChange={() => setDigits(value)}
            />{" "}
            {label}
          </label>
        ))}
      </fieldset>
      <VerticalProblem a={curr.a} b={curr.b} />
      <form onSubmit={submit} className="answer-form">
        <input type="number" value={ans} onChange={(e) => setAns(e.target.value)} autoFocus />
        <button type="submit" style={{ marginLeft: "0.5rem" }}>Submit</button>
      </form>
      {feedback && (
        <div className="math-practice-feedback" style={{ marginBottom: 7 }}>
          {feedback}
        </div>
      )}
      {waitingForNext && (
        <button
          className="math-pill-btn"
          style={{ marginTop: "0.7em" }}
          onClick={() => {
            setCurr(genMulProblem(digits));
            setAns("");
            setFeedback(null);
            setWaitingForNext(false);
          }}
        >
          Next
        </button>
      )}
      <p>Score: {correct} / {attempts}</p>
      {/* Finish Session Button */}
      <button
        className="math-pill-btn"
        style={{ margin: "1em 0" }}
        onClick={finishSession}
      >
        Finish Session
      </button>
      <div className="worksheet-buttons">
        <strong>Printable worksheets:</strong>
        {SHEET_SIZES.map(n => (
          <button
            key={n}
            onClick={() => startSheet(n)}
            style={{ marginLeft: "0.5rem" }}
          >
            {n} Questions
          </button>
        ))}
      </div>
    </div>
  );
}
